import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgClass, NgIf } from '@angular/common';
import { Component, ElementRef, HostBinding, HostListener, inject, Input, Optional, Self, signal } from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl } from '@angular/forms';


/**
 * Custom file input component
 * to match the look and feel of the design
 *
 * @class FileInputComponent
 * @typedef {FileInputComponent}
 * @implements {ControlValueAccessor}
 */
@Component( {
    selector: 'riege-file-input',
    templateUrl: './file-input.component.html',
    styleUrls: ['./file-input.component.scss'],
    imports: [NgIf, NgClass],
    standalone: true
} )
export class FileInputComponent implements ControlValueAccessor {
    /** reference on input change method */
    onChange = ( _: any ) => { };
    /** reference on input touch method */
    onTouched = () => { };

    /** reference on selected file object
     * @ignore
     */
    file: File | null = null;

    /** comma separated list of file extensions */
    @Input() allowedTypes: string[] = [];

    typeOne = $localize`:@@hint-one-file-type:Supported file type is`;
    typeFew = $localize`:@@hint-few-file-types:Supported file types are`;

    /** localized label & hint string */
    @Input() label = '';
    @Input() hint? = '';

    /** touched event
     * @ignore
     */
    readonly touched = signal( false );

    /** reference on html element */
    private readonly _elementRef = inject<ElementRef<HTMLElement>>( ElementRef );

    /** @ignore */
    notGiven = signal( false );
    /** @ignore */
    validType = signal( true );
    /** Whether the component is in an error state. */
    errorState = signal( false );

    /** helper function to create unique input id */
    static nextId = 0;
    @HostBinding() id = `file-input-${FileInputComponent.nextId++}`;

    /** change event listener */
    @HostListener( 'change', ['$event.target.files'] ) emitFiles( event: FileList ) {
        const file = event && event.item( 0 );

        /** when allowed types are given, check the file extension */
        if ( file && this.allowedTypes.length ) {
            const extensionIndex = file.name.lastIndexOf( '.' );
            const extension = file.name.slice( extensionIndex + 1 );

            const isValidType = this.allowedTypes.includes( extension );
            this.validType.set( isValidType );
        } else {
            this.notGiven.set( true );
        }

        this.onChange( file );
        this.file = file;
    }

    /** bind element on NgControl to interact with surrounding form */
    constructor(
        @Optional() @Self() public ngControl: NgControl,
        @Optional() private _parentFormGroup: FormGroupDirective,
        private host: ElementRef<HTMLInputElement>
    ) {
        if ( this.ngControl != null ) {
            this.ngControl.valueAccessor = this;
        }
    }

    /** bind on change detection
     * @ignore
     */
    ngDoCheck() {
        if ( this.ngControl ) {
            this.updateErrorState();
        }
    }

    /** implement focus behaviour
     * @ignore
     */
    focused = signal( false );
    onFocusIn( event: FocusEvent ) {
        if ( !this.focused() ) {
            this.focused.set( true );
        }
    }
    onFocusOut( event: FocusEvent ) {
        if ( !this._elementRef.nativeElement.contains( event.relatedTarget as Element ) ) {
            this.touched.set( true );
            this.onTouched();
            this.focused.set( false );
            this.updateErrorState();
        }
    }

    /** bind on required behaviour
     * @ignore
     */
    @Input()
    get required() {
        return this._required;
    }
    /** @ignore */
    set required( req: BooleanInput ) {
        this._required = coerceBooleanProperty( req );
    }
    /** mirror required property
     * @ignore
     */
    private _required = false;

    /** bind on error reporting behaviour
     * @ignore
     */
    private updateErrorState() {
        const parent = this._parentFormGroup;

        const oldState = this.errorState();
        const noInput = ( this.ngControl?.hasError( 'required' ) || !this.file ) && ( this.touched() || parent.submitted );
        const newState = noInput;

        this.notGiven.set( noInput );

        if ( oldState !== newState ) {
            this.errorState.set( newState );
        }
    }

    /** implement {@link ControlValueAccessor} methods */
    writeValue( value: null ) {
        // clear file input
        this.host.nativeElement.value = '';
        this.file = null;
    }
    /** register change event */
    registerOnChange( fn: any ) {
        this.onChange = fn;
    }

    /** register touched event */
    registerOnTouched( fn: any ) {
        this.onTouched = fn;
    }
}
